import {
  AccordionDetails,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePermissionWrapper } from 'src/components/hooks/usePermissionWrapper';
import { useUser } from 'src/components/hooks/useUser';
import { sendRequest } from 'src/hooks/useFetch';
import { setConfirmExitValue } from 'src/store/confirmExitValue';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { clearMessage, setMessage } from 'src/store/Message';
import { ISIVChecklistClinicalTrialData } from 'src/types/SIVChecklist';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { formatInput } from 'src/util/STR';
import { useFieldTranslation } from '../../fields/useFieldTranslation';

import {
  IClinicalTrialMandatory,
  ISIVChecklistMandatory,
  ISIVChecklistModifiedKeys,
  ISIVChecklistModifiedKeysOfKeys,
  MandatoryData
} from './useSivChecklist';
import moment from 'moment';
import { SERVER_DATE_FORMAT } from 'src/constants/formats';
import { setIsSaveButtonDisable } from 'src/store/sivCheckList';

export interface IClinicalTrialDetails {
  clinicalTrialData: ISIVChecklistClinicalTrialData;
  updateClinicalData: (data: ISIVChecklistClinicalTrialData) => void;
  getSivChecklistData: (key: ISIVChecklistModifiedKeysOfKeys, add: boolean) => void;
  saveDisable: boolean;
  setSaveDisable: (saveDisable: boolean) => void;
  isUpdate: boolean;
  isChange: boolean;
  setIsChange: (step: ISIVChecklistModifiedKeys, key: ISIVChecklistModifiedKeysOfKeys, val: boolean) => void;
  updateSIVChecklistMandatoryData: (key: keyof ISIVChecklistMandatory, value: MandatoryData) => void;
  mandatory: IClinicalTrialMandatory;
  validateclinicalTrialDetails: any;
  checkedInStartDate: any;
  isPublished: boolean;
  SignatureBreaking: () => void;
}

const ClinicalTrialDetails: React.FunctionComponent<IClinicalTrialDetails> = ({
  getSivChecklistData,
  saveDisable,
  setSaveDisable,
  clinicalTrialData,
  updateClinicalData,
  isUpdate,
  setIsChange,
  isChange,
  mandatory,
  updateSIVChecklistMandatoryData,
  validateclinicalTrialDetails,
  checkedInStartDate,
  isPublished,
  SignatureBreaking
}) => {
  const dispatch = useDispatch();
  const dispatchApp = useAppDispatch();
  const { sivcDataUpdateSuccessMsg, sivcMendatoryInfoErrorMsg, checklistClinicalTrailDetails } = useFieldTranslation();

  const studyId: any = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const [protocolVersion, setProtocolVersion] = useState<any>();
  const [artifact04ProtocolVersion, setArtifact04ProtocolVersion] = useState('');
  const [artifact02ProtocolVersion, setArtifact02ProtocolVersion] = useState('');
  const { checkPermission } = usePermissionWrapper();
  const isDisable = checkPermission('update_siv_checklist');
  const user = useUser();
  const selectedRowData: any = useAppSelector(state => state.sivCheckList.selectedRowData);

  const saveButtonDisable: any = useAppSelector(state => state.sivCheckList.disableSaveButtonProps);
  const [loadProtocolVersion, setLoadProtocolVersion] = useState(false);
  const handleClinicalTrialData = (e: any) => {
    dispatchApp(clearMessage());
    const val = e.target.value;
    const formattedVal = typeof val === 'string' ? formatInput(val) : val;
    const value = formattedVal;
    if (e.target.type === 'radio' && value === 'Y') {
      updateClinicalData({
        ...clinicalTrialData,
        siv_checklist_protocol_version_num: protocolVersion?.toString() == 'x.xx' ? null : protocolVersion,
        [e.target.name as keyof ISIVChecklistClinicalTrialData]: value,
        [`${e.target.name}_comm` as keyof ISIVChecklistClinicalTrialData]: ''
      });
      updateSIVChecklistMandatoryData('clinicalTrialDetails', { ...mandatory, [e.target.name]: false });
    } else {
      updateClinicalData({
        ...clinicalTrialData,
        [e.target.name as keyof ISIVChecklistClinicalTrialData]: value
      });
    }
    const validateKey = `${e.target.name.replace('_comm', '')}`;
    if (e.target.name?.indexOf('_comm') !== -1) {
      if (!(value && value !== null && value !== '')) {
        updateSIVChecklistMandatoryData('clinicalTrialDetails', { ...mandatory, [validateKey]: true });
      } else {
        updateSIVChecklistMandatoryData('clinicalTrialDetails', { ...mandatory, [validateKey]: false });
      }
    }
    dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, clinicalTrialDetails: false }));
    setConfirmExitValue(dispatch, true);
  };

  const { getPreferredDateInLocal } = usePreferedDateFormat();

  /* Updating Actual Check in date -- End*/

  const updateClinicalTrialData = async () => {
    const modifiedObj: any = { ...clinicalTrialData };
    if (modifiedObj.siv_checklist_protocol_version_num == '') {
      delete modifiedObj.siv_checklist_protocol_version_num;
    }
    if (modifiedObj.updated_by == null || modifiedObj.updated_by == '') {
      modifiedObj.created_by = user.user;
    }
    if (checkedInStartDate) {
      modifiedObj.siv_visit_date = moment(checkedInStartDate).format(SERVER_DATE_FORMAT);
    }
    const options = {
      site_id: selectedSite,
      study_id: studyId,
      data: {
        created_by: user.user,
        updated_by: user.user,
        monitoring_visit_id: selectedRowData.id,
        siv_checklist: {
          step_one: { clinical_trails: modifiedObj }
        }
      },
      pathVariables: {
        country: selectedCountry,
        appName: process.env.REACT_APP_NAME
      }
    };
    try {
      if (isChange) {
        if (isUpdate) {
          if (
            clinicalTrialData.siv_checklist_protocol_version !== null &&
            clinicalTrialData.siv_checklist_study_objectives !== null &&
            clinicalTrialData.siv_checklist_inclusion_exclusion !== null &&
            clinicalTrialData.siv_checklist_study_design_timelines !== null &&
            clinicalTrialData.siv_checklist_soa !== null &&
            clinicalTrialData.siv_checklist_enrollment_procedure !== null &&
            clinicalTrialData.siv_checklist_lab_procedure !== null &&
            clinicalTrialData.siv_checklist_ip_da !== null &&
            clinicalTrialData.siv_checklist_protocol_deviation !== null &&
            clinicalTrialData.siv_checklist_other_procedures !== null
          ) {
            await sendRequest('update_sivChecklist_data', options);
            if (isPublished) {
              SignatureBreaking();
            }
            getSivChecklistData('clinicalTrialDetails', false);
            setIsChange('Step1', 'clinicalTrialDetails', false);
            dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
            setConfirmExitValue(dispatch, false);
          } else {
            dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
          }
        }
        if (!isUpdate) {
          if (
            clinicalTrialData.siv_checklist_protocol_version !== null &&
            clinicalTrialData.siv_checklist_study_objectives !== null &&
            clinicalTrialData.siv_checklist_inclusion_exclusion !== null &&
            clinicalTrialData.siv_checklist_study_design_timelines !== null &&
            clinicalTrialData.siv_checklist_soa !== null &&
            clinicalTrialData.siv_checklist_enrollment_procedure !== null &&
            clinicalTrialData.siv_checklist_lab_procedure !== null &&
            clinicalTrialData.siv_checklist_ip_da !== null &&
            clinicalTrialData.siv_checklist_protocol_deviation !== null &&
            clinicalTrialData.siv_checklist_other_procedures !== null
          ) {
            await sendRequest('create_sivChecklist_data', options);
            setIsChange('Step1', 'clinicalTrialDetails', false);
            getSivChecklistData('clinicalTrialDetails', true);
            dispatchApp(setMessage({ msgDetails: sivcDataUpdateSuccessMsg, severity: 'success' }));
            setConfirmExitValue(dispatch, false);
          } else {
            dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
          }
        }
      } else if (validateclinicalTrialDetails) {
        dispatchApp(setMessage({ msgDetails: 'No data modified', severity: 'info' }));
      } else {
        dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
      }
    } catch (e) {
      dispatchApp(setMessage({ msgDetails: sivcMendatoryInfoErrorMsg, severity: 'error' }));
    }
    dispatch(setIsSaveButtonDisable({ ...saveButtonDisable, clinicalTrialDetails: true }));
  };

  const getHeighestProtocolVersion = async () => {
    await getProtocolversionForArtifact04();
    await getProtocolVersionForArtifact02();
  };

  useEffect(() => {
    if (artifact04ProtocolVersion.toString() == 'x.xx' && artifact02ProtocolVersion.toString() == 'x.xx') {
      setProtocolVersion('x.xx');
      setLoadProtocolVersion(true);
    } else {
      if (artifact04ProtocolVersion.toString() !== '' && artifact02ProtocolVersion.toString() !== '') {
        const artiFact2: any = parseFloat(artifact02ProtocolVersion) ? parseFloat(artifact02ProtocolVersion) : 0.0;
        const artiFact4: any = parseFloat(artifact04ProtocolVersion) ? parseFloat(artifact04ProtocolVersion) : 0.0;
        if (parseFloat(artiFact4) >= parseFloat(artiFact2)) {
          setProtocolVersion(
            artifact04ProtocolVersion.toString().includes('.')
              ? artifact04ProtocolVersion
              : parseFloat(artifact04ProtocolVersion).toFixed(1)
          );
        } else {
          setProtocolVersion(
            artifact02ProtocolVersion.toString().includes('.')
              ? artifact02ProtocolVersion
              : parseFloat(artifact02ProtocolVersion).toFixed(1)
          );
        }
        setLoadProtocolVersion(true);
      }
    }
  }, [artifact04ProtocolVersion, artifact02ProtocolVersion]);

  async function getProtocolversionForArtifact04() {
    const options = {
      endPointEnv: 'etmf',
      study_id: studyId,
      pathVariables: {
        tmf_artifact_number: '02.01.04',
        latest_version_hyperlink: false,
        appName: process.env.REACT_APP_NAME
      }
    };
    try {
      const result: any = await sendRequest('get_document_artifactname', options);
      const versonNumber = result?.document_data?.tmf_doc_version_number;
      setArtifact04ProtocolVersion(
        versonNumber.toString().includes('.') ? versonNumber : parseFloat(versonNumber).toFixed(1)
      );
    } catch (error) {
      setArtifact04ProtocolVersion('x.xx');
    }
  }

  async function getProtocolVersionForArtifact02() {
    const options = {
      endPointEnv: 'etmf',
      study_id: studyId,
      pathVariables: {
        tmf_artifact_number: '02.01.02',
        latest_version_hyperlink: false,
        appName: process.env.REACT_APP_NAME
      }
    };
    try {
      const result: any = await sendRequest('get_document_artifactname', options);
      const versonNumber = result?.document_data?.tmf_doc_version_number;
      setArtifact02ProtocolVersion(
        versonNumber.toString().includes('.') ? versonNumber : parseFloat(versonNumber).toFixed(1)
      );
    } catch (error) {
      setArtifact02ProtocolVersion('x.xx');
    }
  }

  useEffect(() => {
    if (
      clinicalTrialData.siv_checklist_protocol_version_num == null ||
      clinicalTrialData.siv_checklist_protocol_version_num.toString() == ''
    ) {
      if (!isUpdate) {
        getHeighestProtocolVersion();
      }
    } else {
      setProtocolVersion(parseFloat(clinicalTrialData.siv_checklist_protocol_version_num.toString()).toFixed(1));
      setLoadProtocolVersion(true);
    }
  }, [clinicalTrialData.siv_checklist_protocol_version_num, isUpdate]);
  return (
    <>
      <AccordionDetails className="sq-checklist-tab">
        <Grid container className="checklist-radio-actions">
          <Grid md={12}>
            {!loadProtocolVersion && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item>
                <Typography sx={{ fontSize: '16px' }}>
                  {checklistClinicalTrailDetails.label12}
                  {':'}
                  <span style={{ marginLeft: '5px' }}>
                    {checkedInStartDate
                      ? getPreferredDateInLocal(checkedInStartDate)
                      : clinicalTrialData.siv_visit_date}
                  </span>
                </Typography>
              </Grid>
            </Grid>
            <h4>{checklistClinicalTrailDetails.header1}</h4>
            <Grid container>
              <Grid md={9}>
                {checklistClinicalTrailDetails.label1}{' '}
                {clinicalTrialData.siv_checklist_protocol_version_num?.toString() == ''
                  ? protocolVersion
                  : clinicalTrialData.siv_checklist_protocol_version_num === null
                  ? 'x.xx'
                  : parseFloat(clinicalTrialData.siv_checklist_protocol_version_num.toString()).toFixed(1)}{' '}
                <span style={{ color: 'red' }}>*</span>
              </Grid>
              {/* <Grid md={6}>Protocol Version {protocolVersion==undefined?clinicalTrialData.siv_checklist_protocol_version_num?.toString():protocolVersion.toString()} *</Grid> */}
              {/* <Grid md={6}>Protocol Version {protocolVersion} *</Grid> */}
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_protocol_version"
                    value={clinicalTrialData.siv_checklist_protocol_version}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    onChange={handleClinicalTrialData}
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              <Grid md={9}>
                {' '}
                {checklistClinicalTrailDetails.label2} <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_study_objectives"
                    value={clinicalTrialData.siv_checklist_study_objectives}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    onChange={handleClinicalTrialData}
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              <Grid md={9}>
                Inclusion and exclusion criteria (<Link underline="always">Inclusion and exclusion CRF</Link>){' '}
                <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_inclusion_exclusion"
                    value={clinicalTrialData.siv_checklist_inclusion_exclusion}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    onChange={handleClinicalTrialData}
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              <Grid md={9}>
                {' '}
                {checklistClinicalTrailDetails.label4} <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_study_design_timelines"
                    value={clinicalTrialData.siv_checklist_study_design_timelines}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    onChange={handleClinicalTrialData}
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <h4> {checklistClinicalTrailDetails.header2}</h4>
            <Grid container>
              <Grid md={9}>
                Schedule of assessments (<Link underline="always">SOA calendar</Link>){' '}
                <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_soa"
                    value={clinicalTrialData.siv_checklist_soa}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    onChange={handleClinicalTrialData}
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              {/* Screening and enrollment procedures (IRT manual and screening/ enrollment log) */}
              <Grid md={9}>
                Screening and enrollment procedures (
                <Link underline="always">IRT manual and screening/ enrollment log</Link>){' '}
                <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_enrollment_procedure"
                    value={clinicalTrialData.siv_checklist_enrollment_procedure}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    onChange={handleClinicalTrialData}
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              <Grid md={9}>
                Specimens/samples to be obtained and frequency (<Link underline="always">lab procedures</Link>){' '}
                <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_lab_procedure"
                    value={clinicalTrialData.siv_checklist_lab_procedure}
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    onChange={handleClinicalTrialData}
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              <Grid md={9}>
                Investigational product and drug administration procedures (
                <Link underline="always">
                  Pharmacy procedures, Drug accountability log, Patient diary, drug destruction procedures
                </Link>
                ) <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_ip_da"
                    value={clinicalTrialData.siv_checklist_ip_da}
                    row
                    onChange={handleClinicalTrialData}
                    aria-labelledby="demo-row-radio-buttons-group-label"
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              <Grid md={9}>
                Procedure for recording and reporting protocol deviations (
                <Link underline="always">Protocol deviations CRF</Link>) <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_protocol_deviation"
                    value={clinicalTrialData.siv_checklist_protocol_deviation}
                    row
                    onChange={handleClinicalTrialData}
                    aria-labelledby="demo-row-radio-buttons-group-label"
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container>
              <Grid md={9}>
                {' '}
                {checklistClinicalTrailDetails.label10} <span style={{ color: 'red' }}>*</span>
              </Grid>
              <Grid md={3}>
                <FormControl>
                  <RadioGroup
                    name="siv_checklist_other_procedures"
                    value={clinicalTrialData.siv_checklist_other_procedures}
                    row
                    onChange={handleClinicalTrialData}
                    aria-labelledby="demo-row-radio-buttons-group-label"
                  >
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="Y"
                      control={<Radio />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={!isDisable || saveDisable || !checkedInStartDate}
                      value="N"
                      control={<Radio />}
                      label="No"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
      <Grid container className="fixed-buttons-block">
        <Grid md={12} display="flex" justifyContent="flex-end">
          <Stack spacing={2} direction="row">
            {isDisable ? (
              <Button
                onClick={updateClinicalTrialData}
                variant="text"
                disabled={saveButtonDisable?.clinicalTrialDetails || !checkedInStartDate}
              >
                SAVE
              </Button>
            ) : (
              ''
            )}
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

export default ClinicalTrialDetails;
